import React, { useState, useContext, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useMutation } from '@apollo/client'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { ADD_JOURNAL_ENTRY_MUTATION } from '../query/gqlQueries.js'
import styles from './AddProjectDialog.module.scss'
import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'

// Mood scale — index maps to the stored 1..5 mood value.
const MOODS = [
  { value: 1, emoji: '😞', label: 'Low' },
  { value: 2, emoji: '😕', label: 'Down' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '🙂', label: 'Good' },
  { value: 5, emoji: '😄', label: 'Great' },
]

export default function AddJournalDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [mood, setMood] = useState(null)
  const [tagsInput, setTagsInput] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [source, setSource] = useState('text') // becomes 'voice' if dictated
  const [errorMsg, setErrorMsg] = useState('')

  const recognitionRef = useRef(null)
  const [addJournalEntry, { loading: saving }] = useMutation(ADD_JOURNAL_ENTRY_MUTATION)

  const isOpen = dialogStates['dialogAddJournal']

  // Voice capture appends the transcript to the body — we store only the text,
  // never the audio, which keeps the journal private and light.
  const toggleRecord = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      setErrorMsg('Speech recognition is not supported in this browser.')
      return
    }
    if (isRecording) {
      recognitionRef.current?.stop()
      return
    }
    try { window.speechSynthesis?.cancel() } catch { /* fine */ }
    const rec = new SpeechRecognition()
    rec.lang = 'en-US'
    rec.continuous = true
    rec.interimResults = false
    rec.onstart = () => { setIsRecording(true); setErrorMsg('') }
    rec.onend = () => setIsRecording(false)
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        setErrorMsg('Microphone access was blocked.')
      }
    }
    rec.onresult = (event) => {
      let chunk = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) chunk += event.results[i][0].transcript + ' '
      }
      if (chunk) {
        setSource('voice')
        setBody((prev) => (prev.trim() ? prev.trim() + ' ' : '') + chunk.trim())
      }
    }
    recognitionRef.current = rec
    try { rec.start() } catch { /* fine */ }
  }

  useEffect(() => {
    return () => { try { recognitionRef.current?.abort() } catch { /* fine */ } }
  }, [])

  const handleClose = () => {
    try { recognitionRef.current?.abort() } catch { /* fine */ }
    setTitle(''); setBody(''); setMood(null); setTagsInput('')
    setIsRecording(false); setSource('text'); setErrorMsg('')
    closeDialog('dialogAddJournal')
  }

  const handleSave = async () => {
    if (!body.trim()) { setErrorMsg('Write or record something first.'); return }
    const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean)
    try {
      await addJournalEntry({
        variables: {
          input: {
            username,
            body: body.trim(),
            title: title.trim() || null,
            mood: mood ?? null,
            tags,
            source,
          },
        },
      })
      handleClose()
    } catch (err) {
      console.error('Failed to save journal entry:', err)
      setErrorMsg(err.message || 'Failed to save entry.')
    }
  }

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '620px' }}>
        <button className={styles.closeBtn} onClick={handleClose}>
          <CloseIcon />
        </button>

        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Private · stays on your machines</div>
            <h2 className={styles.title}>New Journal Entry</h2>
          </div>
        </div>

        <div className={styles.container}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Title (optional)</div>
            <input
              type="text"
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="A few words…"
            />
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>What's on your mind?</div>
            <textarea
              className={styles.input}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type freely, or tap the mic to speak…"
              rows={6}
              autoFocus
            />
            <button
              type="button"
              onClick={toggleRecord}
              style={{
                marginTop: '0.6rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                background: isRecording ? 'rgba(248,113,113,0.15)' : 'rgba(110,231,183,0.12)',
                border: `1px solid ${isRecording ? '#f87171' : '#6ee7b7'}`,
                color: isRecording ? '#f87171' : '#6ee7b7',
                borderRadius: '8px', padding: '0.5rem 0.9rem', cursor: 'pointer', fontSize: '0.85rem',
              }}>
              {isRecording ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
              {isRecording ? 'Stop recording' : 'Record your voice'}
            </button>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>How are you feeling? (optional)</div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {MOODS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMood(mood === m.value ? null : m.value)}
                  title={m.label}
                  style={{
                    flex: 1, fontSize: '1.5rem', padding: '0.5rem 0', borderRadius: '10px', cursor: 'pointer',
                    background: mood === m.value ? 'rgba(110,231,183,0.18)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${mood === m.value ? '#6ee7b7' : 'rgba(255,255,255,0.1)'}`,
                    transition: 'all 0.15s',
                  }}>
                  {m.emoji}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Tags (comma-separated, optional)</div>
            <input
              type="text"
              className={styles.input}
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="e.g. work, sleep, relationships"
            />
          </div>

          {errorMsg && <div style={{ color: '#f87171', fontSize: '0.85rem' }}>{errorMsg}</div>}
        </div>

        <div className={styles.footer}>
          <button className={styles.primaryBtn} onClick={handleSave} disabled={saving || !body.trim()}>
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
